import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IConversationMessage, IConversationOptions } from 'src/types/serviceResponseTypes';
import { TUserInputType } from 'src/app/hooks/network/useSendConversation';
import LatexHTMLRenderer from 'src/app/components/LatexHTMLRenderer/LatexHTMLRenderer';
import Explanation from 'src/app/components/Explanation/Explanation';
import Button from 'src/app/components/Button/Button';
import Nudge from 'src/app/components/Nudge/Nudge';
import styles from './MultipleInput.module.scss';
import { useRecoilState } from 'recoil';
import { checkExerciseAtom, conversationsAtom, frameParamsAtom } from '../../../state/atoms';
import captureEvent from 'src/app/lib/gtm-functions';
import { t } from 'i18next';
import { TIMEOUTS } from 'src/app/constants';
import ChoiceItem from '../ChoiceItem';

type MultipleInputPropType = {
  conversation: IConversationMessage;
  isSMEMode?: boolean;
  isHistoryMode?: boolean;
  onInputSelect?: (optionKey: string, userInputType?: TUserInputType) => void;
  isExplanationPinned?: boolean;
  isLastItem?: boolean;
  isSecondLastItem?: boolean;
  shouldFocusExplanation?: boolean;
  onReleaseNextQuestionGate?: () => void;
};

function MultipleInput(props: MultipleInputPropType) {
  const {
    conversation,
    isSMEMode,
    isHistoryMode,
    onInputSelect,
    isExplanationPinned,
    isLastItem,
    isSecondLastItem,
    shouldFocusExplanation,
    onReleaseNextQuestionGate,
  } = props;
  const { is_multi_choice: isMultiChoice } = conversation;

  const [userSelection, setUserSelection] = useState<string[]>();
  const [isCheckAnswerDisabled, setIsCheckAnswerDisabled] = useState(true);
  const firstChoiceInputRef = useRef<HTMLInputElement | null>(null);
  const choicesFieldsetRef = useRef<HTMLFieldSetElement | null>(null);
  const lastFocusKeyRef = useRef<string>('');
  const hasCheckedAnswerRef = useRef(false);
  const [conversations] = useRecoilState(conversationsAtom);
  const [checkExercise] = useRecoilState(checkExerciseAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);

  useEffect(() => {
    setIsCheckAnswerDisabled(true);
    if (isMultiChoice) {
      setUserSelection(() => []);
    }
  }, [conversation.selected_choices, isMultiChoice, conversation.attempts_left]);

  // APAI-2291: return focus to the question's options after checking an answer.
  useEffect(() => {
    if (isSMEMode || isHistoryMode || !isLastItem) return;
    if (conversation.locked || conversation.is_correct) return;
    if (!hasCheckedAnswerRef.current) return;
    // APAI-2290: leave focus alone while the result is being announced.
    if (shouldFocusExplanation || conversation.attempts_left === 0) return;

    const focusKey = `${conversation.id}-${conversation.attempts_left ?? ''}-${
      conversation.selected_choices?.join(',') ?? ''
    }`;
    if (!conversation.selected_choices?.length || lastFocusKeyRef.current === focusKey) return;
    lastFocusKeyRef.current = focusKey;

    const timerId = setTimeout(() => {
      const nextChoice =
        choicesFieldsetRef.current?.querySelector<HTMLInputElement>(
          'input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled)'
        ) ?? firstChoiceInputRef.current;
      nextChoice?.focus();
    }, TIMEOUTS.FEEDBACK_FOCUS_DELAY);
    return () => clearTimeout(timerId);
  }, [
    conversation.id,
    conversation.locked,
    conversation.is_correct,
    conversation.attempts_left,
    conversation.selected_choices,
    isSMEMode,
    isHistoryMode,
    isLastItem,
    shouldFocusExplanation,
  ]);

  const handleCheckAnswer = useCallback(() => {
    if (!conversation.locked && userSelection) {
      hasCheckedAnswerRef.current = true;
      onInputSelect?.(userSelection.join(', '), 'multiple_choice');
      if (onInputSelect) {
        captureEvent({
          event_action: 'Click Check Answer',
          event_category: 'General',
          command_source_code: 'Click',
          question_type: 'MCQ',
          conversation_id: conversations?.data?.id,
          chat_action_id: conversation?.id,
          choice_key: userSelection,
          mlm_book_code: frameParams?.book_code ?? 'N/A',
          app_id: 'MLMAITutor',
          exercise_id: frameParams?.exercise_id,
          user_role: frameParams?.['user-role'],
          person_id_type: frameParams?.person_id_type,
          login_session_id: frameParams?.login_session_id,
        });
      }
      setIsCheckAnswerDisabled(true);
    }
  }, [
    conversation.locked,
    conversation?.id,
    userSelection,
    onInputSelect,
    conversations?.data?.id,
    frameParams,
  ]);

  const handleUserSelection = useCallback(
    (choice: IConversationOptions) => {
      setIsCheckAnswerDisabled(false);

      setUserSelection((prev = []) => {
        let newSelection;
        if (isMultiChoice) {
          if (prev.includes(choice.key)) {
            newSelection = prev.filter((key) => key !== choice.key);
          } else {
            newSelection = [...prev, choice.key];
          }
        } else {
          newSelection = [choice.key];
        }

        captureEvent({
          event_action: 'Click Answer',
          event_label: `${choice.text}`,
          element_type: isMultiChoice ? 'checkbox_button' : 'radio_button',
          element_status: 'selected',
          conversation_id: conversations?.data?.id,
          chat_action_id: conversation?.id,
          choice_key: choice.key,
          mlm_book_code: frameParams?.book_code ?? 'N/A',
          app_id: 'MLMAITutor',
          exercise_id: frameParams?.exercise_id,
          user_role: frameParams?.['user-role'],
          person_id_type: frameParams?.person_id_type,
          login_session_id: frameParams?.login_session_id,
        });

        return newSelection;
      });
    },
    [conversation?.id, conversations?.data?.id, frameParams, isMultiChoice]
  );

  // Memoize correct choices lookup with Set for O(1) performance
  const correctChoicesSet = useMemo(() => {
    if (!conversation?.correct_choice) return new Set<string>();
    return new Set(conversation.correct_choice.split(',').map((x) => x.trim()));
  }, [conversation?.correct_choice]);

  const checkCorrectChoice = useCallback(
    (key: string) => correctChoicesSet.has(key),
    [correctChoicesSet]
  );

  const getSelectionStyle = useCallback(
    (choiceKey: string) => {
      if (checkCorrectChoice(choiceKey)) {
        // Answered/correct row: white fill (Figma). The green state is conveyed by
        // the check mark + green label, not a background tint.
        return 'bg-white';
      }

      if (
        conversation.selected_choices?.includes(choiceKey) &&
        !isMultiChoiceHydration(conversation)
      ) {
        if (!conversation.is_correct) {
          // Incorrect/answered row: white fill (Figma). The incorrect state is
          // conveyed by the red indicator + red label, not a background tint.
          return 'bg-white';
        } else {
          if (!checkCorrectChoice(choiceKey)) {
            return 'bg-white';
          }
        }
      } else if (userSelection?.includes(choiceKey)) {
        // Selected (pre-submit): white row + teal radio + dark text (Figma p8)
        return 'bg-white';
      }

      return 'bg-white';
    },
    [checkCorrectChoice, userSelection, conversation]
  );

  const isMovedOn = useCallback((conversation: IConversationMessage) => {
    if (conversation?.locked) {
      if (!Object.prototype.hasOwnProperty.call(conversation, 'is_correct')) {
        return true;
      }
    }
    return false;
  }, []);

  const isMultiChoiceHydration = useCallback(
    (conversation: IConversationMessage) => {
      if (!isMultiChoice) return false;
      if (conversation?.locked) return false;
      if (conversation?.attempts_left === 0) return false;

      if (
        conversation?.selected_choices?.length === conversation?.choices?.length &&
        conversation?.attempts_left &&
        conversation?.attempts_left > 0
      ) {
        return true;
      }

      return false;
    },
    [isMultiChoice]
  );

  const movedOn = useMemo(() => isMovedOn(conversation), [isMovedOn, conversation]);
  const multiChoiceHydration = useMemo(
    () => isMultiChoiceHydration(conversation),
    [isMultiChoiceHydration, conversation]
  );

  // Memoize choices rendering
  const renderedChoices = useMemo(() => {
    return conversation.choices?.map((choice, index) => (
      <ChoiceItem
        key={`${conversation.id}-${choice.key}`}
        choice={choice}
        conversation={conversation}
        isMultiChoice={!!isMultiChoice}
        userSelection={userSelection}
        isSMEMode={isSMEMode}
        isHistoryMode={isHistoryMode}
        checkCorrectChoice={checkCorrectChoice}
        isMultiChoiceHydration={multiChoiceHydration}
        selectionStyle={getSelectionStyle(choice.key)}
        enablePinning={!!checkExercise.data.enable_pinning}
        onUserSelection={handleUserSelection}
        inputName={`choice-${conversation.id}`}
        isFirstChoice={index === 0}
        firstChoiceInputRef={firstChoiceInputRef}
      />
    ));
  }, [
    conversation,
    isMultiChoice,
    userSelection,
    isSMEMode,
    isHistoryMode,
    checkCorrectChoice,
    multiChoiceHydration,
    getSelectionStyle,
    checkExercise.data.enable_pinning,
    handleUserSelection,
  ]);

  return (
    <>
      <div className={`${styles.tutorBubble} ${styles.multiChoiceInputContainer}`}>
        <div className={movedOn ? 'opacity-50' : ''}>
          <fieldset ref={choicesFieldsetRef}>
            <legend className='grow flex overflow-x-auto overflow-y-hidden'>
              <div className='grow'>
                <p className='font-inter text-body-small text-main overflow-auto '>
                  <LatexHTMLRenderer content={conversation.content} size='small' />
                </p>
              </div>
            </legend>
            {renderedChoices}
          </fieldset>
        </div>
        {movedOn && <p className='font-medium text-lg'>{t('MOVED_ON')}</p>}

        {/* Check answer lives inside the question/options card (Figma: same div) */}
        {isMultiChoice && !conversation.locked && (
          <Button
            buttonText={t('CHECK_ANSWER')}
            className='font-inter-bold !text-button-medium !py-space-2 !mt-space-4 !rounded-full'
            isDisabled={isCheckAnswerDisabled || isSMEMode}
            onClickHandler={handleCheckAnswer}
            ariaHidden={isCheckAnswerDisabled || isSMEMode}
          ></Button>
        )}

        {!conversation.locked && !conversation?.is_correct && !isMultiChoice && (
          <Button
            buttonText={t('CHECK_ANSWER')}
            className='font-inter-bold !text-button-medium !py-space-2 !mt-space-4 !rounded-full'
            isDisabled={isCheckAnswerDisabled || isSMEMode}
            ariaHidden={isCheckAnswerDisabled || isSMEMode}
            onClickHandler={handleCheckAnswer}
          ></Button>
        )}
      </div>

      <div aria-live={isLastItem ? 'assertive' : 'off'} aria-atomic={isLastItem ? 'true' : 'false'}>
        {conversation?.nudge && <Nudge nudge={conversation.nudge} />}
      </div>
      {conversation?.explanation && (
        <Explanation
          explanation={conversation?.explanation ?? ''}
          isCorrect={conversation.is_correct}
          pinGroupId={conversation.id}
          isPinned={isExplanationPinned}
          isSecondLastItem={isSecondLastItem}
          shouldFocus={shouldFocusExplanation}
          onFocusMessage={onReleaseNextQuestionGate}
        />
      )}
    </>
  );
}

export default React.memo(MultipleInput);
